import classNames from 'classnames';
import * as React from 'react';
import { CSSProperties, useMemo, useState } from 'react';
import { CloseIcon } from 'tdesign-icons-react';

import { MediaConfigType } from '@/const/richMedia';
import { UserResourceType } from '@/interface/library';
import { EAsyncStatus } from '@/interface/media';

import { emitRichMedia } from '../change-data';
import LibraryContent from '../core/common/content/library';
import SpineWebPlayer from '../core/player/spine';
import FolderUpload, { ISpineResult, IUploadSpineRef } from '../folder';
import SpineDialog from './dialog';
import styles from './index.module.less';
import SpinePreviewAndEdit from './previewAndEdit';

export interface SpinePlayerRef {
  getAnimation: () => string | undefined;
  getImage: () => Promise<any>;
}

export interface INewUploadIProps {
  uploadSpineRef?: React.ForwardedRef<IUploadSpineRef>;
  value: ISpineResult;
  onChange: (result: ISpineResult) => any;
  asyncUploadOnChange?: (file?: ISpineResult) => any; // 剧情中卸载后处理图片loading恢复
  limit?: {
    fileType?: string[];
    size?: number;
    showSizeMsg?: string;
  };
  disabled?: boolean;
  promptText?: string;
  style?: CSSProperties;
}

export const UploadSpine: React.FC<INewUploadIProps> = (props) => {
  const {
    onChange,
    value,
    style,
    disabled,
    uploadSpineRef,
    asyncUploadOnChange,
  } = props;
  const [visible, setVisible] = useState(false);

  const loading = useMemo(() => {
    if (!asyncUploadOnChange) {
      return false;
    }
    if (value?.asyncStatus === EAsyncStatus.UPLOAD_LOADING) {
      return true;
    }
    return false;
  }, [value?.asyncStatus === EAsyncStatus.UPLOAD_LOADING]);

  const uploadChange = async (result: ISpineResult) => {
    const { localPath, directoryPath } = result;
    if (localPath && directoryPath) {
      emitRichMedia(localPath, directoryPath, MediaConfigType.SPINE);
    }
    onChange(result);
  };

  const libraryChange = (result: ISpineResult) => {
    onChange(result);
  };

  const handleDelete = () => {
    if (disabled) {
      return;
    }
    uploadChange({});
  };

  const handleOpenDialog = () => {
    setVisible(true);
  };

  const handleCloseDialog = () => {
    setVisible(false);
  };

  const handleConfirmDialog = (val: ISpineResult) => {
    onChange(val);
    handleCloseDialog();
  };

  return (
    <div className={styles.uploadWrap} style={{ ...style }}>
      {!value?.skel ? (
        <div className={styles.line}>
          <div className={classNames(styles.item, { [styles.oneItemLoading]: loading })}>
            <FolderUpload
              ref={uploadSpineRef}
              {...props}
              onChange={uploadChange}
              asyncUploadOnChange={asyncUploadOnChange}
            />
          </div>
          {!loading
            && <div className={styles.item}>
              <LibraryContent
                disabled={disabled || loading}
                libraryStyle={{ borderLeft: '1px solid #333333' }}
                onSpineChange={libraryChange}
                type={UserResourceType.RESOURCE_TYPE_SPINE}
              />
            </div>
          }
        </div>
      ) : (
          <>
          <SpinePreviewAndEdit
            value={value}
            disabled={disabled}
            removeIcon={<CloseIcon size={16} />}
            openDialog={handleOpenDialog}
            handleDelete={handleDelete}
          />
            <div
              key={`spine-thumbnail-${value.skel}`}
              style={{
                position: 'absolute',
                opacity: 0,
                width: '1px',
                height: '1px',
                overflow: 'hidden',
                pointerEvents: 'none',
                backgroundColor: 'transparent',
              }}>
              <SpineWebPlayer
                value={value}
                disabled={disabled}
                config={{ width: '100px', height: '100px' }}
                onChange={onChange}
              />
            </div>
          </>
      )}

      <SpineDialog
        config={{ width: 420 }}
        value={value}
        visible={visible}
        disabled={disabled}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDialog}
      />
    </div>
  );
};

export default UploadSpine;
